import React, { useCallback, useEffect, useMemo, useState } from 'react'

import MergeTypeIcon from '@mui/icons-material/MergeType'
import { Grid, Link as MUILink, List, Typography } from '@mui/material'
import {
  ArrayField,
  BooleanField,
  Button,
  Datagrid,
  EditButton,
  FunctionField,
  Labeled,
  RaRecord,
  ReferenceField,
  ReferenceManyField,
  Show,
  SimpleShowLayout,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useDataProvider,
  useGetList,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin'
import { Link, useParams } from 'react-router-dom'

import { ActionIcon, Text, Tooltip } from '@mantine/core'
import { IconCheck, IconClick, IconX } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { GetApiMediaUploaderProjectsProjectIdOrGuidFilesIdOrGuid200 } from '../../codegen/generated/Api.schemas'
import { ListMediaFiles } from '../../components/Artworks/ListMediaFiles'
import { LXNumberField } from '../../components/LXNumberField'
import { LXYoutubeIcon } from '../../components/LXYoutubeIcon'
import { LXYoutubeLink } from '../../components/LXYoutubeLink'
import { ShowPrepayments } from '../../components/Prepayments/ShowPrepayments'
import { API } from '../../helpers/Api.helper'
import { DateHelper } from '../../helpers/Date.helper'
import { DestinationsHelper } from '../../helpers/Destinations.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Artwork } from '../../types/Artwork'
import { Beneficiary } from '../../types/Beneficiary'
import { Prepayment } from '../../types/Prepayments'
import { YoutubeAsset } from '../../types/YoutubeAsset'
import { YoutubeChannel } from '../../types/YoutubeChannel'
import { LXField } from './../../components/LXField'
import { ArtworksDissociateAssetModal } from './ArtworksDissociateModal'
import { ArtworksMergeModal } from './ArtworksMergeModal'

type IMediaFilesData = {
  dubbingType: string
  version: string
  hasFootprint: boolean
}

export const ArtworksShow = (): JSX.Element => {
  const translate = useTranslate()
  const dataProvider = useDataProvider()
  const params = useParams()
  const userStore = useUserStore()

  const [artwork, setArtwork] = useState<Artwork | undefined>()

  // Get artworkName for App title
  useEffect(() => {
    if (!params.id) {
      return
    }

    dataProvider
      .getOne('artworks', { id: params.id as string })
      .then(res => setArtwork(res?.data as Artwork))
  }, [dataProvider, params?.id])

  const ArtworksShowActions = () => (
    <TopToolbar
      style={{
        paddingBottom: 0,
      }}
    >
      <Tooltip
        label={`⚠ ${translate('pages.users.lackingPermission')} → ${translate(
          'pages.users.permissionsList.artworks.edit',
        )}`}
        hidden={userStore.isAuthorised('artworks.edit')}
      >
        <span>
          <EditButton disabled={!userStore.isAuthorised('artworks.edit')} />
        </span>
      </Tooltip>
    </TopToolbar>
  )

  return (
    <Show
      title={`${translate('pages.artworks._title')} / ${artwork?.name ?? ''}`}
      actions={<ArtworksShowActions />}
    >
      <SimpleShowLayout>
        <ArtworksInformations
          id={params.id ?? ''}
          artwork={artwork}
          beneficiaryId={artwork?.product.beneficiaryId ?? ''}
        />
      </SimpleShowLayout>
    </Show>
  )
}

const ArtworksInformations: React.FC<{
  id: Artwork['id']
  artwork: Artwork | undefined
  beneficiaryId: Beneficiary['id']
}> = ({ id, artwork, beneficiaryId }) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()
  const record = useRecordContext()
  const userStore = useUserStore()

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isMergeArtworksModalOpen, setIsMergeArtworksModalOpen] = useState(false)
  const [youtubeChannels, setYoutubeChannels] = useState<YoutubeChannel[]>([])

  const prepaymentsQuery = useQuery<Prepayment[]>({
    queryKey: ['prepayments', artwork?.id],
    queryFn: async () => {
      const response = await API.get<Prepayment[]>('/api/prepayments', {
        params: {
          targetType: 'artwork',
          targetId: artwork?.id,
        },
      })

      return response.data
    },
    enabled: !!artwork?.id,
  })
  const mediaFilesQuery = useGetList<GetApiMediaUploaderProjectsProjectIdOrGuidFilesIdOrGuid200>(
    'mediaUploaderFiles',
    {
      sort: { field: 'id', order: 'ASC' },
      pagination: { page: 1, perPage: 1000 },
      filter: {
        artworkId: artwork?.id,
      },
    },
    {
      enabled: !!artwork?.id,
    },
  )
  const mediaFilesData = useMemo(() => {
    return (mediaFilesQuery.data ?? [])
      .sort((a, b) => (DateHelper.compare(a.createdAt, b.createdAt, 'before') ? 1 : 0))
      ?.reduce((obj, mf) => {
        if (mf.metadata?.dubbingType && !obj.dubbingType) {
          obj.dubbingType = mf.metadata?.dubbingType
        }

        if (mf.metadata?.version && !obj.version) {
          obj.version = mf.metadata?.version
        }

        if (mf.destinationYoutubeFootprint?.completed && !obj.hasFootprint) {
          obj.hasFootprint = true
        }
        return obj
      }, {} as IMediaFilesData)
  }, [mediaFilesQuery.data])

  const onRefreshClaimsClicked = React.useCallback(async () => {
    try {
      setIsRefreshing(true)
      await API.post(`/api/youtubeClaims/refresh?assetId=${record.youtubeAssetId}`, {})
      refresh()
      notify(translate('pages.artworks.toast_refreshClaimsSucceeded'), {
        type: 'success',
      })
    } catch (e: any) {
      notify(`${e.response.data?.message || e.response.data}`, {
        type: 'error',
      })
    }
    setIsRefreshing(false)
  }, [notify, refresh, translate, record])

  const getYoutubeChannels = useCallback(async () => {
    if (record.id) {
      const { data: assetsData } = await API.get<YoutubeAsset[]>(
        `/api/youtubeAssets/?filter={"artworkId":["${record.id}"]}`,
      )
      const assets = new Set(assetsData.map(ad => ad.channelId))

      const { data: channels } = await API.get<YoutubeChannel[]>(
        `/api/youtubeChannels/?filter={"id":[${Array.from(assets)
          .map(a => `"${a}"`)
          .join(',')}]}`,
      )

      setYoutubeChannels(channels)
    }
  }, [record.id])

  const onCloseMergeModal = useCallback(() => {
    setIsMergeArtworksModalOpen(false)
    refresh()
  }, [refresh])

  const ArtworksDissociate = () => {
    const { data } = useListContext()

    if (process.env.REACT_APP_DISABLE_DISSOCIATION !== 'true' && data.length > 1)
      return (
        <FunctionField
          addLabel={false}
          render={(subRecord: RaRecord | undefined) => (
            <ArtworksDissociateAssetModal
              artworkId={id}
              assetId={subRecord?.id.toString() ?? ''}
              cms={youtubeChannels.findLast(yc => yc.cms !== '')?.cms ?? ''}
              beneficiaryId={beneficiaryId}
              productId={artwork?.productId ?? ''}
              onSuccess={() => {
                refresh()
              }}
            />
          )}
          hidden={!userStore.isAuthorised('artworks.edit')}
        />
      )
  }

  useEffect(() => {
    getYoutubeChannels()
  }, [getYoutubeChannels])

  return (
    <Grid container direction="column" spacing={2}>
      {userStore.isAuthorised('artworks.edit') && (
        <Grid
          lg={12}
          container
          item
          alignItems="flex-end"
          justifyContent="flex-end"
          alignContent="flex-end"
        >
          <Button
            label={translate('pages.artworks.mergeArtworks')}
            onClick={() => setIsMergeArtworksModalOpen(true)}
          >
            <MergeTypeIcon />
          </Button>
        </Grid>
      )}
      <Grid container item direction="row" lg={12} spacing={2}>
        <LXField
          source="name"
          label={translate('_generics.name')}
          elementType={TextField}
          size={6}
          labeled
        />
        <LXField
          source="name"
          label={translate('_generics.product')}
          elementType={TextField}
          size={6}
          wrappingFunction={el => (
            <ReferenceField source="productId" reference="products" link="show">
              {el}
            </ReferenceField>
          )}
          labeled
        />
        <LXField
          source="originalArtworkCode"
          label={translate('pages.artworks.artworkCode')}
          elementType={TextField}
          size={6}
          labeled
        />
        <LXField
          source="productionYear"
          label={translate('_generics.productionYear')}
          elementType={TextField}
          labeled
          size={6}
        />
        <LXField label={translate('pages.artworks.availableVersions')} size={6} labeled>
          <Text>{mediaFilesData.version ?? ''}</Text>
        </LXField>
        <LXField label={translate('pages.artworks.dubbingType')} size={6} labeled>
          <Text>{mediaFilesData.dubbingType ?? ''}</Text>
        </LXField>
        <LXField
          source="directorsAndAuthors"
          label={translate('_generics.directorsAndAuthors')}
          elementType={TextField}
          labeled
          size={6}
        />
        <LXField source="ISAN" label="ISAN" elementType={TextField} labeled size={6} />
        <LXField
          source="synopsis"
          label={translate('_generics.synopsis')}
          elementType={TextField}
          labeled
          size={12}
        />
        <LXField label={translate('pages.artworks.hasFootprint')} size={6} labeled>
          {mediaFilesData.hasFootprint === true ? <IconCheck size={16} /> : <IconX size={16} />}
        </LXField>
        <LXField label={translate('pages.artworks.mainTask')} size={6} labeled>
          <FunctionField
            source="clickupTaskId"
            label={translate('pages.artworks.mainTask')}
            render={(record: RaRecord | undefined) =>
              record?.clickupTaskId ? (
                <Link
                  to={DestinationsHelper.getClickUpPath(record.clickupTaskId)}
                  target="_blank"
                  style={{
                    width: 'fit-content',
                  }}
                >
                  <ActionIcon variant="subtle">
                    <IconClick size={20} />
                  </ActionIcon>
                </Link>
              ) : (
                <ActionIcon variant="subtle" disabled>
                  <IconClick size={20} />
                </ActionIcon>
              )
            }
          />
        </LXField>
        <LXField
          source="name"
          label={translate('_generics.youtubeChannels')}
          elementType={TextField}
          size={12}
          labeled
        >
          <>
            {youtubeChannels.map(ytc => (
              <MUILink href={`/youtubeChannels/${ytc.id}/show`} key={ytc.id}>
                <Typography variant="body2">{ytc.name}</Typography>
              </MUILink>
            ))}
          </>
        </LXField>
        <TabbedShowLayout>
          <Tab label={translate('_generics.youtubeAssets')}>
            <ArrayField source="youtubeAssets" label="">
              <Datagrid bulkActionButtons={false}>
                <TextField source="id" label={translate('_generics.assetId')} />
                <TextField source="name" label={translate('_generics.name')} />
                <ReferenceField
                  source="channelId"
                  reference="youtubeChannels"
                  link="show"
                  label={translate('_generics.youtubeChannel')}
                >
                  <TextField source="name" />
                </ReferenceField>
                <FunctionField
                  source="clickupTaskId"
                  label={translate('_generics.subTask')}
                  render={(record: RaRecord | undefined) =>
                    record?.clickupTaskId ? (
                      <Link
                        to={DestinationsHelper.getClickUpPath(record.clickupTaskId)}
                        target="_blank"
                        style={{
                          width: 'fit-content',
                        }}
                      >
                        <ActionIcon variant="subtle">
                          <IconClick size={20} />
                        </ActionIcon>
                      </Link>
                    ) : (
                      <ActionIcon variant="subtle" disabled>
                        <IconClick size={20} />
                      </ActionIcon>
                    )
                  }
                />
                <FunctionField
                  addLabel={false}
                  render={(subRecord: RaRecord | undefined) => (
                    <LXYoutubeLink
                      youtubeAssetId={subRecord?.id.toString()}
                      cms={youtubeChannels.findLast(yc => yc.cms !== '')?.cms}
                    />
                  )}
                />
                <ArtworksDissociate />
              </Datagrid>
            </ArrayField>
          </Tab>
          {record.youtubeAssetId && (
            <Tab label={translate('_generics.youtubeClaims')}>
              <Grid item lg={12}>
                <Labeled label={translate('_generics.youtubeClaims')}>
                  <ReferenceManyField
                    label={translate('_generics.youtubeClaims')}
                    reference="youtubeClaims"
                    source="youtubeAssetId"
                    target="assetId"
                    filter={{ status: 'active' }}
                    sort={{ field: 'videoViews', order: 'DESC' }}
                    perPage={1000}
                    emptyText="nope"
                  >
                    <List>
                      <Datagrid bulkActionButtons={false}>
                        <TextField
                          source="videoTitle"
                          label={translate('pages.youtubeClaims.videoTitle')}
                        />
                        <LXNumberField
                          source="videoViews"
                          format="large"
                          label={translate('pages.youtubeClaims.videoViews')}
                        />
                        <BooleanField
                          source="isPartnerUploaded"
                          label={translate('pages.youtubeClaims.isPartnerUploaded')}
                        />
                        <FunctionField
                          render={(subRecord: any) => (
                            <>
                              {subRecord?.studioInfo.claimUrl && (
                                <MUILink href={subRecord?.studioInfo.claimUrl} target="_blank">
                                  <Button label={translate('pages.youtubeClaims.studioClaimUrl')} />
                                </MUILink>
                              )}
                              {subRecord?.studioInfo.videoUrl && (
                                <MUILink href={subRecord?.studioInfo.videoUrl} target="_blank">
                                  <Button label={translate('pages.youtubeClaims.studioVideoUrl')} />
                                </MUILink>
                              )}
                              {subRecord?.videoId && (
                                <MUILink
                                  href={`https://www.youtube.com/watch?v=${subRecord.videoId}`}
                                  target="_blank"
                                >
                                  <Button label={translate('pages.youtubeClaims.studioVideoYTUrl')}>
                                    <LXYoutubeIcon />
                                  </Button>
                                </MUILink>
                              )}
                            </>
                          )}
                        />
                      </Datagrid>
                    </List>
                  </ReferenceManyField>
                </Labeled>
              </Grid>
              <Button
                label={translate('pages.artworks.refreshClaims')}
                onClick={onRefreshClaimsClicked}
                disabled={isRefreshing}
              />
            </Tab>
          )}
          {(userStore.isAuthorised('artworks.edit') ||
            (prepaymentsQuery.data?.length ?? 0) > 0) && (
            <Tab label={translate('pages.prepayments._title')} path="prepayments">
              <ShowPrepayments targetType="artwork" targetId={id as string} />
            </Tab>
          )}
          {userStore.isAuthorised('media_uploader.access') && (
            <Tab label={translate('pages.mediaFiles._title')} path="mediaFiles">
              <ListMediaFiles artworkId={id as string} />
            </Tab>
          )}
        </TabbedShowLayout>
      </Grid>
      <ArtworksMergeModal
        artworkId={record.id.toString()}
        artworkName={artwork?.name ?? ''}
        beneficiaryId={beneficiaryId}
        isOpen={isMergeArtworksModalOpen}
        onClose={onCloseMergeModal}
      />
    </Grid>
  )
}
